import {IPrMonteCarloDrawImperial, IWesleaResult,} from "@/modules/perRoad/interfaces/monteCarlo";
import {LayerPerformanceCriteriaLocation} from "@/modules/perRoad/interfaces/perRoad";


export interface IHistogramBin {
  binNum: number,
  lowerBound: number,
  midpoint: number,
  upperBound: number,
  count: number,
  cumulativeCount: number,
  pct: number,
  cumulPct: number
}

export const createHistogramBins = (values: number[], nBins: number = 20): IHistogramBin[] => {
  // Bin for histogram creation
  const bins: IHistogramBin[] = [];

  const sortedValues = values.sort((a, b) => a - b);

  const min = Math.min(...sortedValues);
  const max = Math.max(...sortedValues);
  const range = max - min;
  const interval = range / nBins;

  let bn = 0;
  let cumulativeCount = 0;
  const N = sortedValues.length;
  for (let v = min; v < max; v += interval) {
    const lowerBound = v;
    const midpoint = v + interval / 2;
    const upperBound = v + interval;
    const count = sortedValues.filter((x) => x >= lowerBound && x < upperBound).length;
    cumulativeCount += count;
    bins.push({
      binNum: bn,
      lowerBound,
      midpoint,
      upperBound,
      count,
      cumulativeCount,
      pct: count / N,
      cumulPct: cumulativeCount / N,
    });
    bn += 1;
  }

  return bins;
};

export const getPercentileAtValue = (values: number[], targetValues: number[], targetPct: number[]) => {
  const sortedValues = values.sort((a, b) => b - a);
  const cumulativePct = sortedValues.map((v, i) => i / sortedValues.length);

  const pctile = targetValues.map((tv, j) => {
    // Find index closest and greater than target value
    const i = sortedValues.findIndex((v) => v < tv / 1000000);

    if (i === -1 || i === 0) {
      return 1;
    }

    const nextCp = cumulativePct[i];
    const prevCp = cumulativePct[i - 1];

    const nextV = sortedValues[i] * 1000000;
    const prevV = sortedValues[i - 1] * 1000000;

    const m = (nextCp - prevCp) / (nextV - prevV);

    const d = (targetValues[j] - prevV) * m;

    return 1 - (d + prevCp);

    // return 1 - cumulativePct[i];
  });

  return pctile;
};


export const getHorizontalStrainValuesFromWesleaResults = (// wesleaResults: IMonteCarloResultsObject[],
  draws: IPrMonteCarloDrawImperial[],
  // wesleaConfigs: IWesleaConfigBatch,
  // wesleaResults: IWesleaResultBatch,
  layerNum: number,
  depthInLayer: LayerPerformanceCriteriaLocation,
) => {
  // For each run
  const horizontalStrainValues = draws.map((draw) => {

    // console.log("Retrieving horiz strain values for", draw.id, "------------");
    // if (draw.wesleaResult == null) {
    //   return null;
    // }

    // Get wesleaResults for this draw
    const drawResults = draw.wesleaResult; //#wesleaResults[draw.id];
    const wesleaConfig = draw.wesleaConfig;// wesleaConfigs[draw.id];

    // We only care about the bottom of the layer for this analysis.

    let zThreshold = -1;
    // console.log('DepthInLayer', depthInLayer);

    switch (depthInLayer) {
      case LayerPerformanceCriteriaLocation.TOP:
        // console.log('Top', draw);
        zThreshold = draw.layers.filter((layer) => layer.layerNumber === layerNum)[0].depthOfLayerTop_in;
        break;
      case LayerPerformanceCriteriaLocation.MIDDLE:
        // console.log('Middle', draw);
        // @ts-ignore
        const top = draw.layers.filter((layer) => layer.layerNumber === layerNum)[0].depthOfLayerTop_in;
        // @ts-ignore
        const bottom = draw.layers.filter((layer) => layer.layerNumber === layerNum)[0].depthOfLayerBottom_in;
        // console.log('TOpBottom', layerNum, top, (top + bottom) / 2, bottom);
        zThreshold = (top + bottom) / 2;
        break;
      case LayerPerformanceCriteriaLocation.BOTTOM:
        // console.log('Bottom', draw);
        zThreshold = draw.layers.filter((layer) => layer.layerNumber === layerNum)[0].depthOfLayerBottom_in;
        break;
      default:
        zThreshold = 0;
    }

    // zThreshold = draw.layers.filter((layer) => layer.layerNumber === layerNum)
    //   .map((L) => (L.depthOfLayerTop_in))[0];

    // console.log('Zthresh', zThreshold);

    // Get critical locations for layer
    const criticalLocationResults: IWesleaResult[] = [];
    if (wesleaConfig?.criticallocations) {
      wesleaConfig.criticallocations.forEach((critLoc, i) => {
        // console.log('CritLocThres', critLoc.z, zThreshold, Math.abs(critLoc.z - zThreshold));
        if (critLoc.l === layerNum && Math.abs(critLoc.z - zThreshold) < 0.01 && drawResults) {
          const locResult = drawResults[i];
          // console.log("Layer", layerNum, critLoc.i, critLoc.l, critLoc.x, critLoc.y, critLoc.z);
          // console.log("drawResult", drawResults[i]);
          criticalLocationResults.push(locResult);
        }
      });
    }

    // Minimum strains means the most negative value => maximum tension
    const minStrains: number[] = [];

    criticalLocationResults.forEach((result) => {
      let hStrain = {x: 0, y: 0, z: 0};
      if (result) {
        hStrain = result.normal_strain;
      }
      minStrains.push(Math.min(...[hStrain.x, hStrain.y]));
    });
    const minStrain = Math.min(...minStrains);
    // console.log("draw", draw);
    // console.log("MinStrain", minStrain);
    // console.log("Complete horiz strain values for", draw.id, "------------");
    return Math.min(...minStrains);
  });

  // Return inverse so we're working with positive values
  return horizontalStrainValues.map((v) => -v);
};

export const getPeakHorizontalStrain = (wesleaResult: IWesleaResult[], layer = 1) => {
  const xStrains: number[] = [];
  const yStrains: number[] = [];

  wesleaResult.forEach(r => {
    if (r.layer === layer) {
      xStrains.push(r.normal_strain.x);
      yStrains.push(r.normal_strain.y);
    }
  });

  const xMin = Math.min(...xStrains);
  const yMin = Math.min(...yStrains);

  return Math.min(xMin, yMin);
};
